import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Search,
  UserCheck,
  UserX,
  Users as UsersIcon,
} from 'lucide-react';

import {
  memberService,
} from '../services/memberService';

import type {
  MemberInfo,
} from '../types/member.types';

import {
  formatDate,
} from '../../../shared/utils/formatUtils';

import Pagination from '../../../shared/components/Pagination';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

const ITEMS_PER_PAGE = 10;

const AdminPendingMemberPage:
  React.FC = () => {
  const modal =
    useModal();

  const [
    users,
    setUsers,
  ] =
    useState<MemberInfo[]>(
      [],
    );

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    searchTerm,
    setSearchTerm,
  ] = useState('');

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  /**
   * 승인 대기 회원 목록 조회
   */
  const loadData =
    useCallback(
      async (): Promise<void> => {
        try {
          setLoading(true);

          const pendingUsers =
            await memberService
              .getPendingMembers();

          /**
           * 최신 가입순 정렬
           */
          const sortedUsers =
            [
              ...pendingUsers,
            ].sort(
              (
                firstUser,
                secondUser,
              ) =>
                new Date(
                  secondUser.createAt,
                ).getTime() -
                new Date(
                  firstUser.createAt,
                ).getTime(),
            );

          setUsers(
            sortedUsers,
          );
        } catch (
          error: unknown
        ) {
          console.error(
            '대기 회원 데이터 로딩 실패:',
            error,
          );

          setUsers(
            [],
          );

          await modal.alert(
            '대기중인 회원 목록을 불러오지 못했습니다.',
          );
        } finally {
          setLoading(
            false,
          );
        }
      },
      [modal],
    );

  useEffect(() => {
    void loadData();
  }, [loadData]);

  /**
   * 검색 필터
   */
  const filteredUsers =
    useMemo(() => {
      const search =
        searchTerm
          .trim()
          .toLowerCase();

      if (!search) {
        return users;
      }

      return users.filter(
        (user) => {
          const userName =
            user.userName
              ?.toLowerCase() ??
            '';

          const userId =
            user.userId
              ?.toLowerCase() ??
            '';

          const userNickname =
            user.userNickname
              ?.toLowerCase() ??
            '';

          const phoneNumber =
            user.phoneNumber
              ?.toLowerCase() ??
            '';

          return (
            userName.includes(
              search,
            ) ||
            userId.includes(
              search,
            ) ||
            userNickname.includes(
              search,
            ) ||
            phoneNumber.includes(
              search,
            )
          );
        },
      );
    }, [
      users,
      searchTerm,
    ]);

  /**
   * 페이지네이션
   */
  const paginatedUsers =
    useMemo(() => {
      const startIndex =
        (currentPage - 1) *
        ITEMS_PER_PAGE;

      const endIndex =
        startIndex +
        ITEMS_PER_PAGE;

      return filteredUsers.slice(
        startIndex,
        endIndex,
      );
    }, [
      filteredUsers,
      currentPage,
    ]);

  /**
   * 검색어 변경 시
   * 첫 페이지로 이동
   */
  useEffect(() => {
    setCurrentPage(
      1,
    );
  }, [searchTerm]);

  /**
   * 목록 개수 변경으로
   * 현재 페이지가 범위를 벗어나면 보정
   */
  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredUsers.length /
            ITEMS_PER_PAGE,
        ),
      );

    if (
      currentPage >
      totalPages
    ) {
      setCurrentPage(
        totalPages,
      );
    }
  }, [
    currentPage,
    filteredUsers.length,
  ]);

  /**
   * 회원 승인
   */
  const handleApprove =
    async (
      userId: string,
    ): Promise<void> => {
      const confirmed =
        await modal.confirm(
          '이 회원을 승인하시겠습니까?',
        );

      if (!confirmed) {
        return;
      }

      try {
        await memberService
          .approveMember(
            userId,
          );

        await modal.alert(
          '회원이 승인되었습니다.',
        );

        await loadData();
      } catch (
        error: unknown
      ) {
        console.error(
          '회원 승인 실패:',
          error,
        );

        await modal.alert(
          '승인에 실패했습니다. 다시 시도해주세요.',
        );
      }
    };

  /**
   * 회원가입 거절
   */
  const handleReject =
    async (
      userId: string,
    ): Promise<void> => {
      const confirmed =
        await modal.confirm(
          '이 회원의 가입을 거절하시겠습니까?',
        );

      if (!confirmed) {
        return;
      }

      try {
        await memberService
          .rejectMember(
            userId,
          );

        await modal.alert(
          '회원 가입이 거절되었습니다.',
        );

        await loadData();
      } catch (
        error: unknown
      ) {
        console.error(
          '회원 가입 거절 실패:',
          error,
        );

        await modal.alert(
          '거절에 실패했습니다. 다시 시도해주세요.',
        );
      }
    };

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center px-4 py-20">
        <div className="text-base text-gray-600 sm:text-lg">
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full w-full flex-col px-4 pb-6 pt-4 sm:px-6 sm:pb-8 sm:pt-6 lg:px-8 lg:pt-8">
      {/* Header */}
      <header className="mb-5 shrink-0 sm:mb-6">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-black sm:text-3xl">
            대기중인 회원
          </h1>

          <div className="flex items-center gap-2 rounded-full bg-white px-3 py-2 text-sm text-gray-600 shadow-sm ring-1 ring-gray-200 sm:text-base">
            <UsersIcon
              size={18}
            />

            <span className="font-medium">
              {
                filteredUsers.length
              }
              명
            </span>
          </div>
        </div>

        <p className="text-sm text-gray-600 sm:text-base">
          가입 승인 대기중인 회원을
          관리합니다.
        </p>
      </header>

      {/* Search */}
      <section className="mb-5 shrink-0 sm:mb-6">
        <div className="relative max-w-md">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={20}
          />

          <input
            type="search"
            placeholder="이름, 닉네임, 아이디, 전화번호 검색"
            value={
              searchTerm
            }
            onChange={(
              event,
            ) =>
              setSearchTerm(
                event.target
                  .value,
              )
            }
            className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-4 text-sm outline-none transition focus:border-main focus:ring-2 focus:ring-main/10 sm:text-base"
          />
        </div>
      </section>

      {/* Mobile */}
      <section className="flex-1 md:hidden">
        {paginatedUsers.length ===
        0 ? (
          <div className="flex min-h-56 items-center justify-center rounded-xl border border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500">
            {searchTerm.trim()
              ? '검색 결과가 없습니다.'
              : '대기중인 회원이 없습니다.'}
          </div>
        ) : (
          <div className="space-y-3">
            {paginatedUsers.map(
              (user) => (
                <article
                  key={
                    user.id
                  }
                  className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                >
                  <div className="px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="break-words text-base font-semibold text-gray-900">
                          {
                            user.userName
                          }

                          {user.userNickname
                            ? ` (${user.userNickname})`
                            : ''}
                        </p>

                        <p className="mt-1 truncate text-sm text-gray-500">
                          #
                          {
                            user.userId
                          }
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 rounded-xl bg-gray-50 p-3">
                      <div>
                        <p className="text-xs text-gray-500">
                          연락처
                        </p>

                        <p className="mt-1 break-all text-sm text-gray-700">
                          {user.phoneNumber ??
                            '-'}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500">
                          가입일
                        </p>

                        <p className="mt-1 text-sm text-gray-700">
                          {formatDate(
                            user.createAt,
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 border-t border-gray-100 px-4 py-3">
                    <button
                      type="button"
                      onClick={() =>
                        void handleApprove(
                          user.userId,
                        )
                      }
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-green-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-green-600"
                    >
                      <UserCheck
                        size={18}
                      />

                      승인
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        void handleReject(
                          user.userId,
                        )
                      }
                      className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-red-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-red-600"
                    >
                      <UserX
                        size={18}
                      />

                      거절
                    </button>
                  </div>
                </article>
              ),
            )}
          </div>
        )}
      </section>

      {/* Tablet / Desktop */}
      <section className="hidden min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-300 bg-white md:block">
        <div className="h-full overflow-auto">
          <table className="w-full min-w-[820px]">
            <thead className="sticky top-0 z-10 border-b border-gray-300 bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 sm:text-base">
                  회원
                </th>

                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 sm:text-base">
                  연락처
                </th>

                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 sm:text-base">
                  가입일
                </th>

                <th className="px-6 py-3 text-center text-sm font-medium text-gray-700 sm:text-base">
                  액션
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedUsers.length ===
              0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-14 text-center text-sm text-gray-500 sm:text-base"
                  >
                    {searchTerm.trim()
                      ? '검색 결과가 없습니다.'
                      : '대기중인 회원이 없습니다.'}
                  </td>
                </tr>
              ) : (
                paginatedUsers.map(
                  (user) => (
                    <tr
                      key={
                        user.id
                      }
                      className="border-b border-gray-200 transition hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-black sm:text-base">
                            {
                              user.userName
                            }

                            {user.userNickname
                              ? ` (${user.userNickname})`
                              : ''}
                          </div>

                          <div className="mt-1 text-sm font-light text-gray-500">
                            #
                            {
                              user.userId
                            }
                          </div>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 sm:text-base">
                        {user.phoneNumber ??
                          '-'}
                      </td>

                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700 sm:text-base">
                        {formatDate(
                          user.createAt,
                        )}
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              void handleApprove(
                                user.userId,
                              )
                            }
                            className="flex items-center gap-1 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-600"
                            title="승인"
                          >
                            <UserCheck
                              size={18}
                            />

                            <span>
                              승인
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              void handleReject(
                                user.userId,
                              )
                            }
                            className="flex items-center gap-1 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
                            title="거절"
                          >
                            <UserX
                              size={18}
                            />

                            <span>
                              거절
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ),
                )
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pagination */}
      {filteredUsers.length >
        0 && (
        <footer className="mt-5 shrink-0 sm:mt-6">
          <Pagination
            currentPage={
              currentPage
            }
            totalItems={
              filteredUsers.length
            }
            itemsPerPage={
              ITEMS_PER_PAGE
            }
            onPageChange={
              setCurrentPage
            }
          />
        </footer>
      )}
    </div>
  );
};

export default AdminPendingMemberPage;